// Listeners turn speech into utterances with ids and exact end-of-speech times.
//   ServerListener  — our microphone + VAD → 16 kHz WAV for the server's recognisers.
//   BrowserListener — the Web Speech API (fallback when the server has no recogniser).
// App sounds never start an utterance (sfx.appSoundActive gates both).

import type { ClientTranscript } from "@shared/guess/api";
import { appSoundActive } from "../../../lib/sfx";
import { Mic } from "./mic";
import { Vad } from "./vad";
import { encodeWav16k } from "./wav";

export interface Utterance {
  id: string;
  startAt: number;
  endAt: number;
  speechMs: number;
  wav?: Blob;
  transcripts?: ClientTranscript[];
}

export interface ListenerEvents {
  level: (db: number) => void;
  start: (id: string, at: number) => void;
  end: (u: Utterance) => void;
  drop: (id: string) => void;
  error: (code: string) => void;
}

export interface Listener {
  kind: "server" | "browser";
  readonly speaking: boolean;
  on<K extends keyof ListenerEvents>(k: K, fn: ListenerEvents[K]): () => void;
  /** Off: speech is ignored (the level meter keeps working). */
  setActive(on: boolean): void;
  /** Browser recogniser language for the next utterances. */
  setLang?(lang: "ka-GE" | "en-US"): void;
  calibration(): { noiseDb: number; speechDb: number; avgMs: number } | null;
  close(): void;
}

let seq = 0;
export function utteranceId(): string {
  const rnd = Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => b.toString(16).padStart(2, "0")).join("");
  seq = (seq + 1) % 1e6;
  return `u${Date.now().toString(36)}${seq.toString(36)}${rnd}`;
}

class Emitter {
  private map = new Map<string, Set<(...a: any[]) => void>>();
  on(k: string, fn: (...a: any[]) => void) {
    if (!this.map.has(k)) this.map.set(k, new Set());
    this.map.get(k)!.add(fn);
    return () => this.map.get(k)?.delete(fn);
  }
  emit(k: string, ...a: unknown[]) {
    for (const fn of this.map.get(k) ?? []) fn(...a);
  }
}

// ------------------------------------------------------------------ server recognition

export class ServerListener implements Listener {
  kind = "server" as const;
  private em = new Emitter();
  private active = false;
  private current: { id: string; ignored: boolean } | null = null;
  private vad: Vad;
  private off: () => void;

  constructor(
    private mic: Mic,
    calibration?: { noiseDb: number; speechDb: number } | null,
  ) {
    this.vad = new Vad();
    if (calibration) this.vad.calibrate(calibration.noiseDb, calibration.speechDb);
    this.off = mic.onFrame((f, endAt, frameMs) => this.frame(f, endAt, frameMs));
  }

  static async open(calibration?: { noiseDb: number; speechDb: number } | null) {
    return new ServerListener(await Mic.open(), calibration);
  }

  get speaking() {
    return this.vad.isSpeaking;
  }

  on<K extends keyof ListenerEvents>(k: K, fn: ListenerEvents[K]) {
    return this.em.on(k, fn as (...a: any[]) => void);
  }

  setActive(on: boolean) {
    this.active = on;
  }

  calibration() {
    return this.vad.calibration();
  }

  private frame(f: Float32Array, endAt: number, frameMs: number) {
    const events = this.vad.push(f, endAt, frameMs, appSoundActive());
    this.em.emit("level", this.vad.level);
    for (const e of events) {
      if (e.t === "start") {
        this.current = { id: utteranceId(), ignored: !this.active };
        if (!this.current.ignored) this.em.emit("start", this.current.id, e.at);
      } else if (e.t === "drop") {
        if (this.current && !this.current.ignored) this.em.emit("drop", this.current.id);
        this.current = null;
      } else if (e.t === "end") {
        const cur = this.current;
        this.current = null;
        if (!cur || cur.ignored) continue;
        const u: Utterance = { id: cur.id, startAt: e.startAt, endAt: e.endAt, speechMs: Math.round(e.endAt - e.startAt) };
        void encodeWav16k(e.frames, this.mic.sampleRate)
          .then((wav) => this.em.emit("end", { ...u, wav }))
          .catch(() => this.em.emit("error", "encode"));
      }
    }
  }

  close() {
    this.off();
    this.mic.close();
  }
}

// ------------------------------------------------------------------ browser recognition (Web Speech API)

type SR = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
  onspeechstart: ((e: any) => void) | null;
  onaudiostart: (() => void) | null;
};

export function browserRecognitionAvailable(): boolean {
  return typeof window !== "undefined" && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
}

export class BrowserListener implements Listener {
  kind = "browser" as const;
  private em = new Emitter();
  private rec: SR;
  private active = false;
  private closed = false;
  private running = false;
  private lang: "ka-GE" | "en-US";
  private cur: { id: string; startAt: number; lastAt: number; ignored: boolean } | null = null;
  private restartTimer: number | null = null;
  private failures = 0;

  constructor(lang: "ka-GE" | "en-US") {
    const Ctor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this.rec = new Ctor() as SR;
    this.lang = lang;
    this.rec.lang = lang;
    this.rec.continuous = true;
    this.rec.interimResults = true;
    this.rec.maxAlternatives = 3;
    this.rec.onresult = (e) => this.result(e);
    this.rec.onerror = (e) => this.fail(String(e?.error ?? "error"));
    this.rec.onend = () => {
      this.running = false;
      if (!this.closed) this.restart(250);
    };
    this.rec.onaudiostart = () => {
      this.failures = 0;
      this.em.emit("level", -30);
    };
    this.start();
  }

  get speaking() {
    return !!this.cur;
  }

  on<K extends keyof ListenerEvents>(k: K, fn: ListenerEvents[K]) {
    return this.em.on(k, fn as (...a: any[]) => void);
  }

  setActive(on: boolean) {
    this.active = on;
  }

  setLang(lang: "ka-GE" | "en-US") {
    if (lang === this.lang) return;
    this.lang = lang;
    this.rec.lang = lang;
    this.cur = null;
    try {
      this.rec.abort(); // restarts with the new language (onend)
    } catch {
      /* not running */
    }
  }

  calibration() {
    return null;
  }

  private start() {
    if (this.running || this.closed) return;
    try {
      this.rec.start();
      this.running = true;
    } catch {
      this.restart(400);
    }
  }

  private restart(ms: number) {
    if (this.restartTimer !== null) window.clearTimeout(this.restartTimer);
    this.restartTimer = window.setTimeout(() => {
      this.restartTimer = null;
      this.start();
    }, ms);
  }

  private fail(code: string) {
    if (code === "no-speech" || code === "aborted") return;
    if (code === "not-allowed" || code === "service-not-allowed") {
      this.closed = true;
      this.em.emit("error", "denied");
      return;
    }
    this.failures += 1;
    if (this.failures >= 3) this.em.emit("error", code === "network" ? "network" : "stt");
  }

  private result(e: any) {
    const now = performance.now();
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (!this.cur) {
        this.cur = { id: utteranceId(), startAt: now - 400, lastAt: now, ignored: !this.active || appSoundActive() };
        if (!this.cur.ignored) this.em.emit("start", this.cur.id, this.cur.startAt);
      }
      if (!r.isFinal) {
        this.cur.lastAt = now;
        this.em.emit("level", -24);
        continue;
      }
      const cur = this.cur;
      this.cur = null;
      if (cur.ignored) continue;
      const transcripts: ClientTranscript[] = [];
      for (let k = 0; k < r.length; k++) {
        const alt = r[k];
        if (alt?.transcript?.trim()) transcripts.push({ lang: this.lang, text: alt.transcript.trim(), confidence: typeof alt.confidence === "number" && alt.confidence > 0 ? alt.confidence : null });
      }
      // the end of speech is when the words stopped changing, not when the final result came in
      const endAt = Math.min(now, cur.lastAt + 150);
      this.em.emit("end", { id: cur.id, startAt: cur.startAt, endAt, speechMs: Math.round(endAt - cur.startAt), transcripts });
    }
  }

  close() {
    this.closed = true;
    if (this.restartTimer !== null) window.clearTimeout(this.restartTimer);
    try {
      this.rec.onend = null;
      this.rec.abort();
    } catch {
      /* not running */
    }
  }
}
